/**
 * HR help text for the Employee module.
 *
 * The employment record is the spine of the system. Its department, grade,
 * line manager, and hierarchy decide who can see and approve the employee's
 * requests; its payroll, policy plan, and salary decide what the employee is
 * paid; its joining date and probation drive leave quota and formula tags.
 * Personal records (education, documents, relatives) are informational unless
 * noted below.
 */

// Repeated on every sub-record form (bank, address, education, ...).
const shared = {
  employee_id: "The employee this record belongs to.",
  employment_id: "The employment record this belongs to.",
  attachment: "Supporting file kept with this record.",
  description: "Internal note about this record. Employees do not see it.",
  is_active: "Only active records are used and shown on the employee's profile.",
  mobile_no: "Mobile number kept on this record.",
  landline_no: "Landline number kept on this record.",
  phone_no: "Contact number kept on this record.",
};

const personal = {
  salutation_id: "Title used when the employee's name is printed on letters and documents.",
  first_name: "Legal first name as it should appear on payslips, letters, and reports.",
  middle_name: "Middle name, if the employee uses one.",
  last_name: "Legal last name as it should appear on payslips, letters, and reports.",
  father_or_husband_name: "Recorded for official documents and statutory reporting.",
  d_o_b: "Date of birth. It is used for age-based reporting and for documents that print the employee's age.",
  gender_id: "Used by leave types that are limited to one gender, so setting it wrong can hide or wrongly grant leave.",
  marital_status_id: "Kept for records and for benefits that depend on marital status.",
  blood_group_id: "Kept for emergency and medical records.",
  religion_id: "Kept for records and for holidays or leave that depend on it.",
  nationality_id: "Kept for records and statutory reporting.",
  cnic_no: "National identity number. It is used on statutory reports and must be unique per employee.",
  cnic_expiry_date: "Expiry date of the identity card. HR can report on employees whose card is about to expire.",
  cnic_attachment: "Scan of the identity card kept on the employee's file.",
  passport_no: "Passport number, used for travel requests and visa paperwork.",
  passport_expiry_date: "Passport expiry, so travel can be planned before it lapses.",
  passport_attachment: "Scan of the passport kept on the employee's file.",
  driving_license_no: "Driving licence number, where the role requires one.",
  driving_license_expiry_date: "Licence expiry, so renewals can be tracked.",
  driving_license_attachment: "Scan of the driving licence kept on the employee's file.",
  ntn_no: "Tax registration number used on payroll and statutory tax reporting.",
  profile_pic: "Photo shown next to the employee across the system and on their profile.",
  mobile_no: "Primary mobile number used for contact and system notifications.",
  landline_no: "Landline number kept for records.",
  phone_no: "Contact number kept for records.",
  email: "Personal email address kept for records. It is not the login address.",
  official_email: "Company email address. It is used for work notifications and is normally the employee's login.",
  employee_id: "The employee this record belongs to.",
  prev_code: "The employee's code in your previous system. Keep it so old records and reports can still be traced.",
  additional_info: "Any extra detail HR wants kept on the employee's file.",
  description: "Internal note about this record. Employees do not see it.",
  password: "Sets or resets the employee's login password. Leave it empty to keep the current one; entering a value replaces it immediately.",
  role_id: "The role decides what the employee can see and do in the system. Changing it takes effect the next time they log in.",
  is_active: "Only active employees can log in and appear in selection lists. Turning this off blocks access without deleting the record.",
};

const employment = {
  company: "Company this employment belongs to. It decides which branches, departments, payrolls, and policies are available for the employee.",
  employment_id: "The employment record these details belong to.",
  joining_date: "The employee's first working day. It drives leave quota allotment, service length, gratuity eligibility, and any formula based on service, so a wrong date changes both leave and pay.",
  employee_type_id: "Employment category such as permanent or contractual. Payroll rules and reports can be limited by it.",
  contracttype_id: "Type of contract the employee is engaged on.",
  contract_expiry_date: "When the current contract ends. HR can report on contracts due to expire so they are renewed in time.",
  branch_id: "Branch the employee belongs to. It drives branch-based filters, reports, and attendance recalculation by branch.",
  department_id: "Department the employee belongs to. It sets their place in the department hierarchy, which decides who can see and approve their requests.",
  location_id: "Work location. Used for location-based filters and reporting.",
  designation_id: "Job designation. Used in letters, reports, and rules that target a designation.",
  jobtitle_id: "Job title shown on documents where it differs from the designation.",
  grade_id: "Grade decides salary benefits and any entitlement attached to grades, such as travel amounts.",
  linemanager_id: "The employee's approving manager. Requests go to them first, and a manager can see the attendance and requests of everyone reporting to them.",
  is_manager: "Marks this employee as a line manager. They can then see and act on records of employees whose line manager is set to them.",
  is_hod: "Marks this employee as head of department. They can see and act on records of everyone below them in the department hierarchy, not only direct reports.",
  is_final_authority: "Marks this employee as a final authority. Requests that reach the end of their workflow come to them.",
  final_authority_id: "The employee who acts as final authority for this employee's requests.",
  is_on_probation: "While on probation, the employee gets probation leave quota instead of full quota, and leave types that disallow probation are blocked for them.",
  probation_period_month: "Length of probation in months. It sets the probation end date used by leave quota and confirmation-based formulas.",
  exempt_attendance: "Excludes the employee from attendance reports and summaries. Use it for staff whose attendance is not tracked, such as senior management.",
  is_retail: "Marks the employee as retail staff, which makes them eligible for retail-only payroll rules and reports.",
  is_working: "Whether the employee is currently working. Turning it off stops them appearing as an active employee.",
  employee_type: "Employment category recorded on this record.",
  time_slot_id: "Default time slot used when building this employee's roster.",
  leaving_reason: "Why the employee left. It appears on end-of-employment reports.",
  is_tax_exempted: "When on, payroll skips the tax deduction for this employee entirely, regardless of the tax structure.",
  policy_id: "Attendance policy plan applied to this employee, which decides how late, absent, and overtime cases affect their salary.",
  exempt_reason: "Why this employee is exempt. Kept for audit.",
};

const salary = {
  employment_id: "Employee this salary record belongs to.",
  payroll_id: "Payroll this employee is paid through. It decides the salary month, cut-off dates, and which payitems are calculated for them.",
  type: "Monthly pays a fixed salary. Daily Wages and Hourly calculate pay from the days or hours actually worked.",
  payment_method_id: "How the salary is paid, such as bank transfer or cash. It drives the bank transfer listing.",
  currency_id: "Currency the salary is held in.",
  bank_id: "Bank the salary is transferred to.",
  gross_salary: "The employee's monthly gross salary. Most payroll formulas are calculated from this figure, so changing it changes almost every payitem. For a mid-month change use Salary Revision instead, which handles arrears.",
  cash_salary: "Portion of the gross salary paid in cash.",
  bank_salary: "Portion of the gross salary transferred to the bank. Cash and bank together must equal the gross salary.",
  last_salary: "The employee's previous salary, shown for reference.",
  is_tax_exempted: "When on, the payroll run skips the tax deduction for this employee even if the payroll has a tax structure attached.",
  ntn_no: "Tax registration number used on payroll tax reporting for this employee.",
  description: "Internal note about this salary record.",
};

const bank = {
  bank_id: "Bank the employee's salary is transferred to.",
  bank_name: "Name of the bank, where it is entered by hand.",
  bank_branch: "Branch that holds the account.",
  bank_branch_code: "Branch code used on the bank transfer file.",
  bank_account_no: "Account number salary is transferred into. Check it carefully; a wrong number sends the salary to the wrong account.",
  bank_account_title: "Name the account is held in. Banks reject transfers when it does not match the account number.",
  bank_account_type: "Type of account, such as current or savings.",
  bank_effective_date: "Date this account starts being used for salary transfer. Payslips paid before this date still use the previous account.",
  is_active: "Only the active account is used for salary transfer. Turning off an old account keeps it on file without paying into it.",
};

const address = {
  address_type: "Whether this is the permanent or current address. Letters and documents pick the type they need.",
  address: "Street address kept on the employee's file.",
  complete_address: "Full address as it should be printed on letters and documents.",
  country_id: "Country of this address.",
  state_id: "State or province of this address.",
  city_id: "City of this address.",
  country: "Country of this address.",
  city: "City of this address.",
  is_active: "Only the active address is used on letters and documents.",
};

const contact = {
  contact_type: "What this contact is for, such as an emergency contact.",
  contact_person_name: "Person to contact. For an emergency contact this is who the company calls first.",
  contact_person_no: "Phone number for this contact.",
  contact_person_email: "Email address for this contact.",
  relationship: "How this person is related to the employee.",
  is_active: "Only active contacts are shown when someone needs to reach the employee's contacts.",
};

const relative = {
  employee_relative_id: "The relative this record is about.",
  name: "Relative's full name as it should appear on records and benefit claims.",
  relationship: "How this person is related to the employee.",
  is_dependant: "Marks the relative as financially dependent, which is what benefit and medical entitlements are usually based on.",
  relative_within_group: "Set this when the relative also works in the company or group. It lets HR see related employees when checking conflicts of interest.",
  employment_id: "The relative's own employment record, when they work in the company.",
  d_o_b: "Relative's date of birth, used for age-based benefit limits.",
  cnic_no: "Relative's identity number, kept for benefit claims.",
  is_active: "Only active relatives are considered for benefits and records.",
};

const education = {
  degree: "Qualification obtained, such as Bachelors or Masters.",
  institute_name: "School, college, or university that awarded it.",
  from_date: "When the employee started this qualification.",
  to_date: "When the employee finished, or is expected to finish.",
  is_degree_completed: "Whether the qualification was completed. Incomplete qualifications are reported separately.",
  degree_attachment: "Scan of the degree or transcript kept on the employee's file.",
  description: "Any note about this qualification.",
  is_active: "Only active records appear on the employee's education profile.",
};

const certification = {
  name: "Name of the certification, such as a professional or safety certificate.",
  institute_name: "Body that issued the certification.",
  certification_date: "Date the certification was issued.",
  expiry_date: "When the certification lapses. HR can report on certifications due to expire so they are renewed in time.",
  attachment: "Scan of the certificate kept on the employee's file.",
  description: "Any note about this certification.",
};

const experience = {
  employer_name: "Previous employer's name.",
  employer_address: "Previous employer's address, used when references are checked.",
  company_name: "Previous employer's name.",
  designation: "Role the employee held there.",
  from_date: "When the employee started at that employer.",
  to_date: "When the employee left. Together with the start date this is the service counted as previous experience.",
  experience_letter: "Scan of the experience or relieving letter kept on the employee's file.",
  last_salary: "Salary the employee drew at that employer. HR uses it when benchmarking an offer.",
  currency_id: "Currency that previous salary was paid in.",
  is_working: "Tick this while the employee is still working at that employer, which leaves the end date open.",
  contact_person_name: "Person at the previous employer who can confirm this experience.",
  contact_person_no: "Phone number for the reference contact.",
  contact_person_email: "Email address for the reference contact.",
  leaving_reason: "Why the employee left that employer.",
  description: "Any note about this experience.",
  is_active: "Only active records count towards the employee's total previous experience.",
};

const document = {
  additional_info: "Any extra detail about this document.",
  document_type_id: "What kind of document this is. It decides where the document appears on the employee's file.",
  name: "Name for this document so HR can recognize it in the list.",
  expiry_date: "When the document lapses, so HR can chase a renewal before it does.",
  file_attachment: "The document file itself, kept on the employee's record.",
  attachment: "The document file itself, kept on the employee's record.",
  description: "Any note about this document.",
};

const asset = {
  asset_type_id: "Category of the asset, such as laptop or vehicle.",
  asset_id: "The specific asset being allocated. An asset already allocated to someone else cannot be issued again until it is returned.",
  issue_date: "Date the asset was handed to the employee. The asset counts as allocated from this date.",
  deallocation_date: "Date the asset was returned. Filling it releases the asset so it can be issued to someone else; leaving it empty keeps the asset allocated to this employee.",
  handed_over_to: "Who the asset was returned to. Kept so responsibility is traceable.",
  handed_over_to_description: "Any note about the handover, such as the condition of the asset.",
  description: "Any note about this allocation.",
  attachment: "Handover document or photo kept with the allocation.",
};

const importEmployees = {
  file_attachment: "Excel file of employees to import. Use the sample file so the columns match; rows whose department, designation, or grade do not already exist are rejected.",
  company: "Company the imported employees are created under.",
};

// The employee profile screens mix personal, employment, and salary fields in one
// form, so they read from a merged dictionary. Personal wording wins for keys that
// exist in more than one group (name, email, is_active).
const profile = { ...shared, ...address, ...contact, ...education, ...salary, ...employment, ...personal };

// The payroll & policy tab shows the payroll setup together with the policy plan.
const payrollPolicy = { ...shared, ...employment, ...salary };

export const employeeTooltips = {
  profile,
  payrollPolicy,
  personal: { ...shared, ...personal },
  employment: { ...shared, ...employment },
  salary: { ...shared, ...salary },
  bank: { ...shared, ...bank },
  address: { ...shared, ...address },
  contact: { ...shared, ...contact },
  relative: { ...shared, ...relative },
  education: { ...shared, ...education },
  certification: { ...shared, ...certification },
  experience: { ...shared, ...experience },
  document: { ...shared, ...document },
  asset: { ...shared, ...asset },
  importEmployees: { ...shared, ...importEmployees },
};

export default employeeTooltips;
